import React, {useState, useEffect, useCallback, useMemo, forwardRef} from 'react'
import {Popover, PopoverTrigger, PopoverContent} from '../../../common/Popover'
import FontPicker from 'font-picker-react'
import {removeNode, addNode, updateNode, reorder} from '../../editorActions'
import Icon from '../../../common/Icon'
import {
    BrushIcon,
    OffsetPathIcon,
    HorizontalFlipIcon,
    VerticalFlipIcon,
    TransparentIcon,
    TrashIcon,
    DuplicateIcon,
    BringToFrontIcon,
    SendToBackIcon,
    AlignLeftIcon,
    AlignCenterIcon,
    AlignRightIcon,
} from '../../../common/icons'
import OffsetPathPopover from './OffsetPathPopover'

const GOOGLE_FONTS_LIST_URL = 'https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity'
const FALLBACK_FONT_FAMILIES = [
    'Arial',
    'Helvetica',
    'Georgia',
    'Times New Roman',
    'Verdana',
    'Courier New',
    'Trebuchet MS',
    'Impact',
    'Comic Sans MS',
]

const ToolbarButton = forwardRef(function ToolbarButton(
    {icon: IconComp, title, onClick, disabled, active, className},
    ref,
) {
    return (
        <button
            ref={ref}
            type="button"
            className={`SelectionToolbar__Button${active ? ' is-active' : ''}${className ? ` ${className}` : ''}`}
            onClick={onClick}
            disabled={disabled}
            title={title}
            aria-label={title}
        >
            <Icon icon={IconComp} className="SelectionToolbar__Icon" />
        </button>
    )
})

function ToolbarDivider() {
    return <div className="SelectionToolbar__Divider" />
}

function ToolbarGroup({children}) {
    return <div className="SelectionToolbar__Group">{children}</div>
}

function FontFamilySelect({node, dispatch}) {
    const style = node.style || {}
    const fontFamily = style.fontFamily ?? 'Arial'
    const [googleFontsAvailable, setGoogleFontsAvailable] = useState(null)

    const googleFontApiKey = process.env.REACT_APP_GOOGLE_FONT_API_KEY || ''
    const fontPickerId = `selToolbarFont${String(node.id).replace(/[^a-zA-Z0-9]/g, '') || '0'}`

    useEffect(() => {
        if (!googleFontApiKey) {
            setGoogleFontsAvailable(false)
            return
        }
        let cancelled = false
        const url = `${GOOGLE_FONTS_LIST_URL}&key=${encodeURIComponent(googleFontApiKey)}`
        fetch(url, {method: 'GET', mode: 'cors'})
            .then((res) => {
                if (!cancelled) setGoogleFontsAvailable(res.ok)
            })
            .catch(() => {
                if (!cancelled) setGoogleFontsAvailable(false)
            })
        return () => {
            cancelled = true
        }
    }, [googleFontApiKey])

    const handleFontChange = useCallback(
        async (font) => {
            const family = font?.family ?? font
            if (!family) return
            await document.fonts.ready
            dispatch(updateNode(node.id, {style: {...style, fontFamily: family}}))
        },
        [dispatch, node.id, style],
    )

    if (!googleFontApiKey) {
        return (
            <div className="SelectionToolbar__FontSelect" title="Google Fonts API key is missing">
                <span className="SelectionToolbar__FontLabel">{fontFamily}</span>
            </div>
        )
    }

    if (googleFontsAvailable === true) {
        return (
            <div className="SelectionToolbar__FontSelect">
                <FontPicker
                    apiKey={googleFontApiKey}
                    pickerId={fontPickerId}
                    activeFontFamily={fontFamily}
                    onChange={handleFontChange}
                />
            </div>
        )
    }

    return (
        <div className="SelectionToolbar__FontSelect">
            <select
                className="SelectionToolbar__FontFallback"
                value={fontFamily}
                onChange={(e) => handleFontChange(e.target.value)}
                aria-label="Font family"
            >
                {[...new Set([fontFamily, ...FALLBACK_FONT_FAMILIES.filter((f) => f !== fontFamily)])].map((f) => (
                    <option key={f} value={f}>
                        {f}
                    </option>
                ))}
            </select>
        </div>
    )
}

function FlipToolbarActions({node, dispatch}) {
    const handleFlipH = () => {
        const geo = node.geometry || {}
        const currentScaleX = geo.scaleX ?? 1
        dispatch(updateNode(node.id, {geometry: {...geo, scaleX: currentScaleX * -1}}))
    }

    const handleFlipV = () => {
        const geo = node.geometry || {}
        const currentScaleY = geo.scaleY ?? 1
        dispatch(updateNode(node.id, {geometry: {...geo, scaleY: currentScaleY * -1}}))
    }

    return (
        <>
            <ToolbarDivider />
            <ToolbarGroup>
                <ToolbarButton icon={HorizontalFlipIcon} title="Flip horizontal" onClick={handleFlipH} />
                <ToolbarButton icon={VerticalFlipIcon} title="Flip vertical" onClick={handleFlipV} />
            </ToolbarGroup>
        </>
    )
}

function TextToolbarActions({node, dispatch}) {
    const style = node.style || {}
    const align = style.align ?? 'left'

    const handleAlign = (value) => {
        dispatch(updateNode(node.id, {style: {...style, align: value}}))
    }

    return (
        <>
            <FontFamilySelect node={node} dispatch={dispatch} />
            <ToolbarDivider />
            <ToolbarGroup>
                <ToolbarButton
                    icon={AlignLeftIcon}
                    title="Align left"
                    onClick={() => handleAlign('left')}
                    active={align === 'left'}
                />
                <ToolbarButton
                    icon={AlignCenterIcon}
                    title="Align center"
                    onClick={() => handleAlign('center')}
                    active={align === 'center'}
                />
                <ToolbarButton
                    icon={AlignRightIcon}
                    title="Align right"
                    onClick={() => handleAlign('right')}
                    active={align === 'right'}
                />
            </ToolbarGroup>
        </>
    )
}

const SelectionToolbar = ({selectionId, state, dispatch, runtime, onRemoveBg}) => {
    const [offsetPathOpen, setOffsetPathOpen] = useState(false)
    const [initialStrokeWidth, setInitialStrokeWidth] = useState(0)

    const node = useMemo(() => {
        if (!selectionId || !state?.nodes) return null
        return state.nodes.get(selectionId) ?? null
    }, [selectionId, state?.nodes])

    if (!node) return null

    const nodeType = node.type
    const isImage = nodeType === 'image'
    const isText = nodeType === 'text'
    const order = state?.order || []

    const handleDelete = () => {
        dispatch(removeNode(selectionId))
    }

    const handleDuplicate = () => {
        const newId = crypto.randomUUID()
        const geo = node.geometry || {}
        const cloned = {
            ...JSON.parse(JSON.stringify(node)),
            id: newId,
            geometry: {
                ...geo,
                x: (geo.x || 0) + 20,
                y: (geo.y || 0) + 20,
            },
        }
        dispatch(addNode(cloned, 0, true))
    }

    const handleBringToFront = () => {
        dispatch(reorder(selectionId, 0))
    }

    const handleSendToBack = () => {
        dispatch(reorder(selectionId, order.length - 1))
    }

    const strokeWidth = node.style?.strokeWidth ?? 0

    const handleOffsetPathOpenChange = (open) => {
        if (open) setInitialStrokeWidth(strokeWidth)
        setOffsetPathOpen(open)
    }

    const handleOffsetPathChange = (value) => {
        dispatch(updateNode(selectionId, {style: {...(node.style || {}), strokeWidth: value}}))
    }

    const handleOffsetPathApply = () => {
        setOffsetPathOpen(false)
    }

    const handleOffsetPathCancel = () => {
        dispatch(updateNode(selectionId, {style: {...(node.style || {}), strokeWidth: initialStrokeWidth}}))
        setOffsetPathOpen(false)
    }

    return (
        <div className="SelectionToolbar">
            {/* Text-specific: font family + alignment (before common) */}
            {isText && <TextToolbarActions node={node} dispatch={dispatch} />}

            {/* Common: Brush + Offset path */}
            <ToolbarGroup>
                {/* <ToolbarButton icon={BrushIcon} title="Fill color" onClick={() => {}} /> */}
                <Popover
                    isOpen={offsetPathOpen}
                    onOpenChange={handleOffsetPathOpenChange}
                    placement="bottom"
                    shadow="lg"
                >
                    <PopoverTrigger>
                        <ToolbarButton icon={OffsetPathIcon} title="Offset path" active={offsetPathOpen} />
                    </PopoverTrigger>
                    <PopoverContent>
                        <OffsetPathPopover
                            value={initialStrokeWidth}
                            onChange={handleOffsetPathChange}
                            onClose={handleOffsetPathCancel}
                            onApply={handleOffsetPathApply}
                        />
                    </PopoverContent>
                </Popover>
            </ToolbarGroup>

            {/* Flip H/V (image + shape) */}
            {!isText && <FlipToolbarActions node={node} dispatch={dispatch} />}

            <ToolbarDivider />

            {/* Common: Layer order */}
            <ToolbarGroup>
                <ToolbarButton icon={BringToFrontIcon} title="Bring to front" onClick={handleBringToFront} />
                <ToolbarButton icon={SendToBackIcon} title="Send to back" onClick={handleSendToBack} />
            </ToolbarGroup>

            {/* Image-specific: remove bg */}
            {isImage && (
                <>
                    <ToolbarDivider />
                    <ToolbarButton
                        icon={TransparentIcon}
                        title="Remove background"
                        onClick={() => onRemoveBg?.(node)}
                    />
                </>
            )}

            {/* Duplicate (all types) */}
            <ToolbarDivider />
            <ToolbarButton icon={DuplicateIcon} title="Duplicate" onClick={handleDuplicate} />

            <ToolbarDivider />
            <ToolbarButton icon={TrashIcon} title="Delete" onClick={handleDelete} />
        </div>
    )
}

export default SelectionToolbar


